import React, { useState, useEffect, useRef } from 'react';
import Modal from 'components/Modal';
import { validatePhone, formatPhoneNumber } from 'utils/validatePhone';
import { sendToGHL } from 'services/ghl';
import { getAccountID, getWebhookID } from 'services/wp';
import { withSubmissionTimeout, getSubmissionErrorMessage } from 'utils/submissionTimeout';
import logo from 'images/logo/home-logo.png';
import { FaDownload, FaCheckCircle, FaShieldAlt, FaArrowDown } from 'react-icons/fa';

const EXACT_TCPA_DISCLAIMER = "I agree to the Terms & Conditions and Privacy Policy. By checking this box and clicking 'I Agree & Get My Free Guide', I provide my express written consent for Insurance Enterprise USA to contact me regarding health insurance options via email, phone calls, and text messages at the number provided above, including automated technology. Consent is not a condition of purchase.";

const EbookForm = () => {
  // Form State
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [errors, setErrors] = useState({});
  const [submissionError, setSubmissionError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Modal States
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Scroll & Checkbox Unlocking States inside Consent Modal
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [modalCheckboxChecked, setModalCheckboxChecked] = useState(false);

  // TCPA Audit Data State
  const [ipAddress, setIpAddress] = useState('Detecting...');

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.ip) {
          setIpAddress(data.ip);
        } else {
          setIpAddress('Unavailable');
        }
      })
      .catch(() => setIpAddress('Unavailable'));
  }, []);

  // Reset scroll position and checkbox state every time consent modal opens
  useEffect(() => {
    if (showConsentModal) {
      setHasScrolledToBottom(false);
      setModalCheckboxChecked(false);
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }, 50);
    }
  }, [showConsentModal]);

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === 'phone') {
      value = formatPhoneNumber(value);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (submissionError) {
      setSubmissionError('');
    }
  };

  const triggerDirectPdfDownload = () => {
    try {
      const pdfUrl = '/The_Insiders_Guide_To_Alternative_Health_Insurance.pdf';
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.setAttribute('download', 'The_Insiders_Guide_To_Alternative_Health_Insurance.pdf');
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.warn('Browser auto-download prevented:', err);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required.';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required.';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required.';
    } else if (validatePhone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit mobile phone number.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Called when user clicks "Get My Free Guide" -> Opens TCPA Consent Pop-up Modal
  const handleInitialSubmit = (e) => {
    e.preventDefault();
    setSubmissionError('');

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setSubmissionError('No internet connection detected. Please check your network connection and try again.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    // Reset modal scroll & checkbox states
    setHasScrolledToBottom(false);
    setModalCheckboxChecked(false);
    setShowConsentModal(true);
  };

  // Detect when user scrolls legal document to the bottom
  const handleLegalScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Check if scrolled within 20px of bottom
    if (scrollHeight - scrollTop - clientHeight <= 25) {
      setHasScrolledToBottom(true);
    }
  };

  // Called when user clicks "I Agree & Get My Free Guide" inside Pop-up Modal
  const confirmConsentAndSubmit = async () => {
    if (!modalCheckboxChecked) return;

    setShowConsentModal(false);
    setIsLoading(true);
    setSubmissionError('');

    const auditPayload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      timestamp: new Date().toISOString(),
      ipAddress: ipAddress !== 'Detecting...' ? ipAddress : '0.0.0.0',
      formUrl: window.location.href,
      userAgent: navigator.userAgent,
      tcpaConsentCaptured: true,
      exactDisclaimerText: EXACT_TCPA_DISCLAIMER,
    };

    console.log('[TCPA POPUP AUDIT LOG RECORDED]:', auditPayload);

    try {
      await withSubmissionTimeout(async () => {
        const accountID = await getAccountID();
        const webhookID = await getWebhookID({ formName: 'ebook' });

        if (!accountID || !webhookID) {
          throw new Error('Missing configuration: Unable to fetch webhook endpoint ID.');
        }

        await sendToGHL(auditPayload, accountID, webhookID);
      });

      // Trigger direct PDF download and success modal upon clean submission
      triggerDirectPdfDownload();
      setShowSuccessModal(true);

      // Reset form fields
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      });
      setErrors({});
    } catch (err) {
      console.error('[EBOOK SUBMISSION ERROR]:', err);
      setSubmissionError(getSubmissionErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto bg-base-100 p-6 sm:p-8 rounded-lg shadow-md border border-base-200">
      {/* Brand Header with Logo */}
      <div className="flex flex-col items-center justify-center mb-6 text-center">
        <img
          src={logo}
          alt="Insurance Enterprise Logo"
          className="h-12 w-auto mb-3 object-contain"
        />
        <h2 className="text-xl font-bold text-base-content">
          Download The Insider's Guide
        </h2>
        <p className="text-xs text-base-content/70 mt-1">
          Fill out the form below to access your free guide.
        </p>
      </div>

      {/* Submission Error Alert */}
      {submissionError && (
        <div className="alert alert-error text-sm text-base-100 mb-4 flex items-center justify-between shadow-sm">
          <span>⚠️ {submissionError}</span>
          <button
            type="button"
            className="btn btn-ghost btn-xs text-base-100"
            onClick={() => setSubmissionError('')}
          >
            ✕
          </button>
        </div>
      )}

      {/* Clean 1-Column Form */}
      <form className="space-y-4" onSubmit={handleInitialSubmit} noValidate>
        {/* First Name */}
        <div className="w-full" data-invalid={errors.firstName ? "true" : undefined}>
          <label className="label py-1 flex justify-between">
            <span className="label-text font-semibold text-xs text-base-content">First Name <span className="text-error">*</span></span>
            {errors.firstName && <span className="text-[10px] font-bold text-white bg-red-600 px-1.5 py-0.5 rounded">Required</span>}
          </label>
          <input
            required
            className={`input input-bordered w-full ${errors.firstName ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
            type="text"
            name="firstName"
            placeholder="Enter your first name"
            value={formData.firstName}
            onChange={handleInputChange}
          />
          {errors.firstName && (
            <span className="text-xs font-medium text-error mt-1 block">{errors.firstName}</span>
          )}
        </div>

        {/* Last Name */}
        <div className="w-full" data-invalid={errors.lastName ? "true" : undefined}>
          <label className="label py-1 flex justify-between">
            <span className="label-text font-semibold text-xs text-base-content">Last Name <span className="text-error">*</span></span>
            {errors.lastName && <span className="text-[10px] font-bold text-white bg-red-600 px-1.5 py-0.5 rounded">Required</span>}
          </label>
          <input
            required
            className={`input input-bordered w-full ${errors.lastName ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
            type="text"
            name="lastName"
            placeholder="Enter your last name"
            value={formData.lastName}
            onChange={handleInputChange}
          />
          {errors.lastName && (
            <span className="text-xs font-medium text-error mt-1 block">{errors.lastName}</span>
          )}
        </div>

        {/* Email Address */}
        <div className="w-full" data-invalid={errors.email ? "true" : undefined}>
          <label className="label py-1 flex justify-between">
            <span className="label-text font-semibold text-xs text-base-content">Email Address <span className="text-error">*</span></span>
            {errors.email && (
              <span className="text-[10px] font-bold text-white bg-red-600 px-1.5 py-0.5 rounded">
                {!formData.email.trim() ? 'Required' : 'Invalid Email'}
              </span>
            )}
          </label>
          <input
            required
            className={`input input-bordered w-full ${errors.email ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
            type="email"
            name="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleInputChange}
          />
          {errors.email && (
            <span className="text-xs font-medium text-error mt-1 block">{errors.email}</span>
          )}
        </div>

        {/* Phone Number */}
        <div className="w-full" data-invalid={errors.phone ? "true" : undefined}>
          <label className="label py-1 flex justify-between">
            <span className="label-text font-semibold text-xs text-base-content">Phone <span className="text-error">*</span></span>
            {errors.phone && (
              <span className="text-[10px] font-bold text-white bg-red-600 px-1.5 py-0.5 rounded">
                {!formData.phone.trim() ? 'Required' : 'Invalid Phone'}
              </span>
            )}
          </label>
          <input
            required
            className={`input input-bordered w-full ${errors.phone ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
            type="tel"
            name="phone"
            placeholder="Enter your number"
            value={formData.phone}
            onChange={handleInputChange}
          />
          {errors.phone && (
            <span className="text-xs font-medium text-error mt-1 block">{errors.phone}</span>
          )}
        </div>

        {Object.keys(errors).length > 0 && (
          <div className="p-3 bg-red-50 border-2 border-red-600 rounded-lg text-red-900 text-xs font-medium">
            ⚠️ Please fix the highlighted fields above before continuing.
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className={`btn btn-secondary w-full text-base-100 mt-4 uppercase font-bold tracking-wider ${
            isLoading ? 'btn-disabled opacity-60' : ''
          }`}
        >
          {isLoading ? (
            <span className="loading loading-spinner loading-md"></span>
          ) : (
            'Get My Free Guide'
          )}
        </button>
      </form>

      {/* Pop-up Modal: Terms & Privacy Consent Agreement (Wider Size max-w-5xl) */}
      <Modal open={showConsentModal} handleClose={() => setShowConsentModal(false)} maxWidth="sm:max-w-5xl">
        <div className="p-4 sm:p-6 text-left w-full mx-auto">
          {/* Modal Title */}
          <div className="flex items-center space-x-3 mb-3 text-secondary border-b border-base-200 pb-3">
            <FaShieldAlt className="text-2xl" />
            <div>
              <h3 className="text-lg font-bold text-base-content leading-tight">
                Terms and Conditions &amp; Privacy Policy Agreement
              </h3>
              <p className="text-xs text-base-content/70">
                Please scroll to the bottom of both documents to unlock and accept the agreement.
              </p>
            </div>
          </div>

          {/* Scrollable Container with Terms and Conditions & Privacy Policy */}
          <div
            ref={scrollContainerRef}
            onScroll={handleLegalScroll}
            className="bg-base-200/60 p-5 rounded-lg border border-base-300 max-h-[380px] overflow-y-auto text-xs leading-relaxed text-base-content/85 space-y-6 shadow-inner select-text"
          >
            {/* --- SECTION 1: TERMS AND CONDITIONS --- */}
            <div className="space-y-3">
              <h4 className="text-sm font-extrabold text-base-content border-b border-base-300 pb-1">
                TERMS AND CONDITIONS OF USE
              </h4>
              <p className="font-semibold text-xs text-base-content/70">Effective Date: August 4, 2026 | Website: insuranceenterpriseusa.com</p>
              
              <p><strong>1. Acceptance of Terms:</strong> Welcome to Insurance Enterprise USA ("Company," "we," "us," or "our"). These Terms and Conditions ("Terms") govern your access to and use of the website located at insuranceenterpriseusa.com, including all subdomains, online quote forms, calculators, digital resources, and related services (collectively, the "Site"). By accessing, browsing, requesting a quote, submitting information, or otherwise using the Site, you agree to be bound by these Terms. If you do not agree to these Terms, you must discontinue your use of the Site.</p>
              
              <p><strong>2. Scope of Services and Entity Disclosure:</strong> Insurance Enterprise USA operates as a licensed insurance entity subject to applicable federal and state insurance laws and regulations. The Site is provided as an informational resource and digital intake platform that allows individuals, families, employers, and organizations to request insurance quotes and learn about available insurance products and services. Submitting an inquiry, quote request, or application through the Site does not create an insurance binder, policy, or guarantee of coverage. All insurance coverage is subject to eligibility requirements, underwriting review, carrier approval, and the issuance of a policy by the applicable insurance carrier.</p>

              <p><strong>3. User Eligibility and Accurate Information:</strong> By using this Site, you represent and warrant that: You are at least eighteen (18) years of age; You have the legal authority to submit information for yourself or for the person or organization you represent; All information you provide is accurate, current, complete, and truthful. Providing false, misleading, or fraudulent information may result in denial of services and any other remedies available under applicable law.</p>

              <p><strong>4. Intellectual Property and Acceptable Use:</strong> All content available on the Site, including text, graphics, logos, images, software, design elements, trademarks, and other materials, is owned by or licensed to Insurance Enterprise USA and is protected by applicable intellectual property laws. You agree not to: Copy, reproduce, distribute, or create derivative works from Site content without prior written permission; Use automated tools, bots, scrapers, crawlers, or similar technologies to collect Site content or data; Attempt to gain unauthorized access to the Site or its systems; Interfere with the operation, integrity, or security of the Site; or Use the Site for any unlawful purpose.</p>

              <p><strong>5. Website Information and Disclaimer:</strong> The information, educational materials, quote tools, calculators, and other resources provided on this Site are offered solely for general informational purposes. Any quote or premium estimate generated through the Site is a non-binding estimate based upon the information provided by the user. Final eligibility, premiums, benefits, policy terms, and coverage determinations are made solely by the applicable insurance carrier through its underwriting process. Although we strive to maintain accurate and current information, Insurance Enterprise USA does not warrant that the Site will always operate uninterrupted, be error-free, or remain continuously available.</p>

              <p><strong>6. Limitation of Liability:</strong> To the fullest extent permitted by applicable law, Insurance Enterprise USA, together with its affiliates, officers, directors, employees, agents, licensors, and service providers, shall not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages arising out of or relating to your access to, use of, or inability to use the Site, even if advised of the possibility of such damages. Nothing in these Terms excludes or limits liability where such exclusion or limitation is prohibited by applicable law.</p>

              <p><strong>7. Force Majeure:</strong> Insurance Enterprise USA shall not be responsible for any delay or failure to perform its obligations under these Terms when such delay or failure results from events beyond its reasonable control, including but not limited to natural disasters, severe weather, fire, flood, pandemic, epidemic, labor disputes, governmental actions, war, terrorism, civil unrest, utility failures, internet or telecommunications disruptions, cyber incidents, or other unforeseen events beyond our reasonable control. Performance shall resume as soon as reasonably practicable following the end of the applicable event.</p>

              <p><strong>8. Third-Party Services:</strong> The Site may contain links to, integrate with, or reference third-party websites, software applications, insurance carriers, or other external services for your convenience. Insurance Enterprise USA does not own or control these third-party services and is not responsible for their availability, content, accuracy, privacy practices, security, products, or services. Your use of third-party services is governed solely by the terms and policies of those providers.</p>

              <p><strong>9. Indemnification:</strong> To the extent permitted by applicable law, you agree to indemnify, defend, and hold harmless Insurance Enterprise USA, its affiliates, officers, directors, employees, agents, licensors, contractors, and service providers from and against any third-party claims, liabilities, damages, judgments, losses, costs, expenses, and reasonable attorneys' fees arising out of or relating to: Your violation of these Terms; Your misuse of the Site; Information submitted by you that is false, misleading, or unlawful; Your violation of applicable law; or Your infringement of the rights of any third party. This obligation survives termination of your use of the Site.</p>

              <p><strong>10. Suspension or Termination of Access:</strong> Insurance Enterprise USA reserves the right to suspend, restrict, or terminate access to the Site, in whole or in part, at any time and for any lawful reason, including where we reasonably believe a user has violated these Terms, engaged in fraudulent or unlawful conduct, compromised Site security, or interfered with the operation of the Site. Termination shall not affect any rights or obligations that accrued before termination.</p>

              <p><strong>11. No Professional Advice:</strong> The information available on this Site is provided solely for general informational purposes. Nothing on the Site constitutes legal, tax, accounting, financial, medical, or other professional advice. Users should consult appropriately qualified professionals regarding their individual circumstances before making decisions regarding insurance or related matters. No information provided through the Site creates an attorney-client, fiduciary, physician-patient, or other professional relationship.</p>

              <p><strong>12. Reservation of Rights:</strong> Insurance Enterprise USA reserves all rights not expressly granted under these Terms. We may modify, suspend, discontinue, or update any portion of the Site, its content, functionality, products, or services at any time, subject to applicable law. Such modifications shall not affect rights or obligations that accrued before the effective date of the modification.</p>

              <p><strong>13. Dispute Resolution:</strong> The parties agree to make a good-faith effort to resolve any dispute informally before initiating legal proceedings. Except where prohibited by applicable law or where arbitration is determined to be unenforceable, disputes arising out of or relating to these Terms shall be resolved through binding arbitration on an individual basis. If a court of competent jurisdiction determines arbitration is unenforceable for a particular claim, that claim shall be brought exclusively in the state or federal courts located in the jurisdiction where Insurance Enterprise USA maintains its principal place of business, and the parties consent to the personal jurisdiction and venue of those courts. Nothing in these Terms limits any non-waivable rights or remedies available under applicable law.</p>

              <p><strong>14. Regulatory Compliance and Governing Law:</strong> These Terms shall be governed by and construed in accordance with applicable federal law and the laws of the state in which Insurance Enterprise USA maintains its principal place of business, without regard to conflict-of-law principles. Insurance Enterprise USA operates in compliance with applicable state insurance laws and federal regulations. Nothing in these Terms limits any consumer protections that cannot be waived under applicable law.</p>

              <p><strong>15. Severability:</strong> If any provision of these Terms is determined by a court of competent jurisdiction to be invalid, illegal, or unenforceable, that provision shall be modified only to the extent necessary to make it enforceable, and the remaining provisions shall remain in full force and effect.</p>

              <p><strong>16. Entire Agreement:</strong> These Terms, together with any policies expressly incorporated by reference, constitute the entire agreement between you and Insurance Enterprise USA regarding your use of the Site and supersede all prior or contemporaneous understandings relating to the Site.</p>

              <p><strong>17. Changes to These Terms:</strong> Insurance Enterprise USA may revise these Terms from time to time. Updated versions become effective upon posting to the Site unless otherwise stated. Continued use of the Site after the effective date of any revision constitutes acceptance of the revised Terms.</p>
            </div>

            {/* --- SECTION 2: COMMUNICATIONS AGREEMENT --- */}
            <div className="space-y-3 pt-4 border-t border-base-300">
              <h4 className="text-sm font-extrabold text-base-content border-b border-base-300 pb-1">
                COMMUNICATIONS AGREEMENT
              </h4>
              <p className="font-semibold text-xs text-base-content/70">Effective Date: August 4, 2026</p>

              <p><strong>1. Communications Consent:</strong> By voluntarily providing your telephone number(s), mobile number(s), email address(es), fax number(s), or other contact information to Insurance Enterprise USA, you expressly consent to receive communications from us regarding your insurance inquiries, applications, policies, products, services, and related matters. Subject to applicable law, such communications may be made by: Live representatives; Artificial or prerecorded voice messages; Automatic telephone dialing systems or similar automated technology; SMS or MMS text messages; Email; Fax; and Other electronic communication methods. Your consent applies to the contact information you provide and remains effective unless and until revoked in accordance with this Agreement or applicable law.</p>

              <p><strong>2. Purpose of Communications:</strong> Communications may include: Insurance quotes; Enrollment assistance; Application status; Policy servicing; Renewal reminders; Coverage updates; Billing notifications; Customer support; Appointment scheduling; Educational information relating to insurance products; Marketing communications where permitted by law.</p>

              <p><strong>3. No Condition of Purchase:</strong> Your consent to receive automated calls or text messages is not a condition of purchasing any insurance product or service. You may obtain information or request insurance assistance by calling 888-350-6605 without providing automated communications consent.</p>

              <p><strong>4. Telephone Consumer Protection Act (TCPA):</strong> Where applicable, you acknowledge that communications may be made using automatic telephone dialing technology, prerecorded or artificial voice technology, or similar systems consistent with the Telephone Consumer Protection Act (TCPA), applicable FCC regulations, and other governing law.</p>

              <p><strong>5. Electronic Communications:</strong> You agree that electronic communications, notices, disclosures, and records satisfy any legal requirement that such communications be provided in writing to the extent permitted by the Electronic Signatures in Global and National Commerce Act (E-SIGN Act) and other applicable law.</p>

              <p><strong>6. SMS Terms:</strong> Message frequency may vary. Message and data rates may apply. Wireless carriers are not responsible for delayed or undelivered messages. Participation is subject to your wireless carrier's terms and conditions.</p>

              <p><strong>7. Revoking Consent:</strong> You may revoke or modify your communications preferences at any time, subject to reasonable processing time and applicable law. You may opt out using any of the following methods: Text Messages: Reply STOP to any SMS message; Email: Click the Unsubscribe link contained in applicable marketing emails; Telephone or Written Request: Phone: 888-350-6605 | Email: enroll@insuranceenterpriseusa.com. Revocation of consent for marketing communications does not prevent us from sending communications necessary to administer your existing policy, complete requested transactions, respond to your inquiries, comply with legal obligations, or otherwise communicate as permitted by law.</p>

              <p><strong>8. Changes to This Agreement:</strong> Insurance Enterprise USA reserves the right to modify this Communications Agreement to reflect changes in applicable law, regulatory guidance, business practices, or communication technologies. Updated versions become effective upon posting with a revised Effective Date.</p>
            </div>

            {/* --- SECTION 3: PRIVACY POLICY --- */}
            <div className="space-y-3 pt-4 border-t border-base-300">
              <h4 className="text-sm font-extrabold text-base-content border-b border-base-300 pb-1">
                PRIVACY POLICY
              </h4>
              <p className="font-semibold text-xs text-base-content/70">Effective Date: August 4, 2026</p>

              <p><strong>1. Information We Collect:</strong> When you interact with Insurance Enterprise USA ("Company," "we," "us," or "our") through our website, online quote forms, enrollment tools, email, telephone, text messaging, or other communications, we may collect information you voluntarily provide, including: Name and contact information; Mailing address; Date of birth; Insurance-related information; Demographic information; Information necessary to evaluate eligibility, obtain insurance quotes, process applications, enroll coverage, administer policies, or respond to your requests; Any other information you choose to provide during the application or enrollment process. Where applicable, this information may include personal information or protected information necessary for insurance underwriting and servicing. We may also use browser local storage and cookies to remember site preferences and maintain operational functionality.</p>

              <p><strong>2. How We Use Your Information:</strong> We may use the information we collect to: Provide insurance quotes; Evaluate eligibility for insurance products; Process applications and enrollments; Verify identity; Communicate regarding quotes, applications, policies, renewals, billing, or customer service; Meet contractual, regulatory, legal, and compliance obligations; Improve our website, products, and services; Detect fraud, unauthorized activity, or security incidents; Maintain business records and operational functions.</p>

              <p><strong>3. Sharing of Information:</strong> We may disclose information, as permitted or required by applicable law, to: Licensed insurance carriers; General agencies; Third-party administrators; Technology and service providers performing services on our behalf; Regulatory agencies; Government authorities when legally required; Professional advisors, auditors, or legal counsel; Successors in connection with a merger, acquisition, or sale of business assets. We do not sell your personal information to third parties for their independent marketing purposes unless permitted by law or authorized by you. Any third-party service providers receiving information on our behalf are expected to protect such information through appropriate contractual or legal obligations.</p>

              <p><strong>4. Privacy Compliance:</strong> Insurance Enterprise USA is committed to protecting your information in accordance with applicable federal and state privacy laws. Where applicable, we comply with the Health Insurance Portability and Accountability Act (HIPAA), the Gramm-Leach-Bliley Act (GLBA), and other applicable privacy and insurance regulations governing the information we collect and maintain. Nothing in this Privacy Policy expands or limits any rights or obligations established under applicable law.</p>

              <p><strong>5. Information Security:</strong> We maintain administrative, technical, and physical safeguards designed to protect personal information against unauthorized access, disclosure, alteration, or destruction. Although we employ commercially reasonable security measures, no method of electronic transmission or storage can be guaranteed to be completely secure. Accordingly, we cannot guarantee absolute security.</p>

              <p><strong>6. Data Retention:</strong> We retain personal information only for as long as reasonably necessary to: Provide requested services; Comply with legal and regulatory obligations; Resolve disputes; Enforce agreements; and Maintain appropriate business records.</p>

              <p><strong>7. Your Privacy Rights:</strong> Subject to applicable federal and state law, you may request to: Access certain personal information we maintain; Correct inaccurate information; Update your contact preferences; Request deletion where legally permitted; or Inquire about our privacy practices. Certain information may be retained where required by law or for legitimate business purposes.</p>

              <p><strong>8. Changes to This Privacy Policy:</strong> We may revise this Privacy Policy from time to time. Any changes become effective upon posting the updated version with a revised Effective Date unless otherwise required by law.</p>
            </div>

            {/* --- SECTION 3: CONSOLIDATED CONTACT INFORMATION --- */}
            <div className="pt-4 border-t border-base-300 space-y-1 bg-base-100 p-3 rounded border border-base-300">
              <h4 className="text-xs font-bold text-base-content uppercase">Contact Information</h4>
              <p className="font-semibold text-xs">Insurance Enterprise USA</p>
              <p>Phone: 888-350-6605</p>
              <p>Email: enroll@insuranceenterpriseusa.com</p>
              <p>Website: insuranceenterpriseusa.com</p>
            </div>
          </div>

          {/* Scroll Prompt Notice if not scrolled to bottom */}
          {!hasScrolledToBottom && (
            <div className="flex items-center justify-center space-x-2 text-warning text-xs font-semibold mt-2 animate-bounce">
              <FaArrowDown className="text-sm" />
              <span>Please scroll to the bottom of the document to unlock the agreement checkbox</span>
            </div>
          )}

          {/* Agreement Checkbox (Locked until scrolled to bottom) */}
          <div className="pt-3 mt-2 border-t border-base-200">
            <label className={`label cursor-pointer flex items-start space-x-3 ${!hasScrolledToBottom ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input
                type="checkbox"
                disabled={!hasScrolledToBottom}
                checked={modalCheckboxChecked}
                onChange={(e) => setModalCheckboxChecked(e.target.checked)}
                className="checkbox checkbox-primary rounded mt-1 cursor-pointer"
              />
              <span className="label-text text-xs leading-relaxed text-base-content/90 font-medium">
                Please review and agree to our Terms &amp; Conditions and Privacy Policy before accessing your free guide:
                <span className="block text-[11px] text-base-content/70 mt-1">
                  By checking this box and clicking "I Agree &amp; Get My Free Guide", I provide my express written consent for Insurance Enterprise USA to contact me regarding health insurance options via email, phone calls, and text messages at the number provided above, including automated technology. <strong className="text-base-content font-bold">Consent is not a condition of purchase.</strong>
                </span>
              </span>
            </label>
          </div>

          {/* Action Buttons (Normal Size) */}
          <div className="flex items-center justify-end space-x-3 mt-4 pt-3 border-t border-base-200">
            <button
              onClick={() => setShowConsentModal(false)}
              className="btn btn-ghost btn-sm text-base-content/70"
            >
              Cancel
            </button>
            <button
              disabled={!modalCheckboxChecked || isLoading}
              onClick={confirmConsentAndSubmit}
              className={`btn btn-secondary btn-sm px-6 text-base-100 font-bold uppercase text-xs tracking-wider ${
                !modalCheckboxChecked || isLoading ? 'btn-disabled opacity-50' : ''
              }`}
            >
              {isLoading ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                'I Agree & Get My Free Guide'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Instant Asset Delivery Success Modal */}
      <Modal open={showSuccessModal} handleClose={() => setShowSuccessModal(false)}>
        <div className="p-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-success/20 text-success mb-3">
            <FaCheckCircle className="text-3xl" />
          </div>
          <h3 className="text-xl font-bold text-base-content mb-2">Your Download Has Started!</h3>
          <p className="text-xs text-base-content/80 mb-5 leading-relaxed">
            Your copy of <strong>The Insider's Guide to Alternative Health Insurance</strong> is downloading automatically. If it doesn't open, click below.
          </p>

          <button
            onClick={triggerDirectPdfDownload}
            className="btn btn-secondary w-full text-white flex items-center justify-center space-x-2"
          >
            <FaDownload />
            <span>Click Here to Re-Download PDF</span>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EbookForm;
